/**
 * Alerts ▸ System Alerts for the site picked in the title bar — the same page the site shows
 * itself (shared SystemAlertsView): its system alerts (CPU / memory / disk, cameras not streaming)
 * and system errors (services that stopped), read and acknowledged on the site through its tunnel.
 *
 * The cloud's OWN system alerts are a different page (user menu ▸ System Alerts, /cloud-system-alerts).
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import { nodeEscalationsApi } from '../services/api.service';
import { PageTitle, SystemAlertsView, Callout } from '@trinetra/shared';

export default function SiteSystemAlertsPage() {
  const { selectedNode } = useNode();
  const { hasPermission } = useAuth();
  const nodeId = selectedNode?.nodeId || null;
  const api = useMemo(() => (nodeId ? nodeEscalationsApi(nodeId) : null), [nodeId]);

  if (!selectedNode || !api) {
    return (
      <div className="space-y-3">
        <PageTitle title="System Alerts" subtitle="One site's machines, cameras and services" />
        <Callout tone="info">Pick a site in the title bar to see its system alerts.</Callout>
      </div>
    );
  }

  const online = selectedNode.status === 'online';
  return (
    <div className="space-y-3">
      <PageTitle title="System Alerts" subtitle={`${selectedNode.name} — its machines, cameras and services: what is wrong now, and what was`}>
        {hasPermission('alerts.config') && (
          <Link to={`/alert-actions?tab=actions&site=${encodeURIComponent(selectedNode.nodeId)}`}
            className="inline-flex items-center rounded-lg border border-border px-3 py-1.5 text-[13px] text-muted-foreground hover:bg-muted">
            Who gets notified
          </Link>
        )}
      </PageTitle>
      <SystemAlertsView
        key={selectedNode.nodeId}
        api={api}
        canEdit={hasPermission('alerts.edit')}
        unavailable={online ? undefined : (
          <>
            <b>{selectedNode.name}</b> is offline, so its system alerts cannot be read right now — they are kept on the site and show here once it reconnects.
            {' '}That it went offline is on <Link to="/system-alerts" className="font-medium text-brand hover:underline">every site&apos;s System Alerts</Link>.
          </>
        )}
      />
    </div>
  );
}
